import React, { useEffect, useState } from 'react';
import {
  Archive,
  AttachFile,
  Business,
  Delete,
  Email,
  Forward,
  Home,
  Message,
  MoreVert,
  Notifications,
  Person,
  Phone,
  Reply,
  Send,
  Star,
  StarBorder,
  VideoCall,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import {
  useGetMessagesQuery,
  useMarkMessageAsReadMutation,
  useSendMessageMutation,
  type Message as ApiMessage,
} from '@/apis/extended/messageApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';

interface Message {
  id: string;
  from: {
    name: string;
    role: 'buyer' | 'seller' | 'agent';
    avatar?: string;
  };
  to: {
    name: string;
    role: 'buyer' | 'seller' | 'agent';
  }[];
  subject: string;
  content: string;
  timestamp: string;
  priority: 'high' | 'medium' | 'low';
  status: 'sent' | 'delivered' | 'read';
  attachments?: string[];
  isRead: boolean;
  isStarred: boolean;
  isArchived: boolean;
}

interface CommunicationHubProps {
  userRole: 'buyer' | 'seller' | 'agent' | 'seller_agent';
  userName: string;
  contractId: string;
}

const CommunicationHub: React.FC<CommunicationHubProps> = ({
  userRole,
  userName,
  contractId,
}) => {
  // Get current user
  const { data: currentUser } = useGetCurrentUserQuery();

  // Message API hooks
  const { data: messagesData, isLoading: messagesLoading } =
    useGetMessagesQuery(contractId, {
      skip: !contractId,
    });
  const [sendMessageMutation] = useSendMessageMutation();
  const [markAsReadMutation] = useMarkMessageAsReadMutation();

  const apiMessages = messagesData?.messages || [];
  const [messageUiById, setMessageUiById] = useState<
    Record<string, { isStarred?: boolean; isArchived?: boolean }>
  >({});
  const [hiddenMessageIds, setHiddenMessageIds] = useState<Set<string>>(
    () => new Set()
  );

  const messages: Message[] = React.useMemo(() => {
    return apiMessages
      .filter((m) => !hiddenMessageIds.has(m._id))
      .map((apiMsg: ApiMessage) => {
        const ui = messageUiById[apiMsg._id];
        return {
          id: apiMsg._id,
          from: {
            name: apiMsg.fromUserId?.name || 'Unknown',
            role: (apiMsg.fromUserId?.role || 'user') as
              | 'buyer'
              | 'seller'
              | 'agent',
          },
          to: apiMsg.toUserId
            ? [
                {
                  name: apiMsg.toUserId.name || 'Unknown',
                  role: (apiMsg.toUserId.role || 'user') as
                    | 'buyer'
                    | 'seller'
                    | 'agent',
                },
              ]
            : apiMsg.toRole
            ? [
                {
                  name: apiMsg.toRole,
                  role: apiMsg.toRole as 'buyer' | 'seller' | 'agent',
                },
              ]
            : [],
          subject: apiMsg.subject || '',
          content: apiMsg.message,
          timestamp: apiMsg.createdAt,
          priority: 'medium' as 'high' | 'medium' | 'low',
          status: apiMsg.read ? 'read' : 'delivered',
          isRead: apiMsg.read,
          isStarred: ui?.isStarred ?? false,
          isArchived: ui?.isArchived ?? false,
        };
      });
  }, [apiMessages, messageUiById, hiddenMessageIds]);

  const [composeDialogOpen, setComposeDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [filter, setFilter] = useState('all');
  const [newMessage, setNewMessage] = useState({
    to: '',
    toRole: '' as 'buyer' | 'seller' | 'agent' | '',
    subject: '',
    content: '',
    priority: 'medium' as 'high' | 'medium' | 'low',
  });

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'buyer':
        return <Person />;
      case 'seller':
        return <Home />;
      case 'agent':
        return <Business />;
      default:
        return <Person />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'buyer':
        return 'primary';
      case 'seller':
        return 'secondary';
      case 'agent':
        return 'success';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.content.trim()) {
      toast.error('Message content is required');
      return;
    }

    if (!newMessage.toRole && !newMessage.to) {
      toast.error('Please select a recipient');
      return;
    }

    try {
      await sendMessageMutation({
        contractId,
        toRole: newMessage.toRole || undefined,
        message: newMessage.content,
        subject: newMessage.subject || undefined,
      }).unwrap();

      toast.success('Message sent successfully');
      setComposeDialogOpen(false);
      setNewMessage({
        to: '',
        toRole: '',
        subject: '',
        content: '',
        priority: 'medium',
      });
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to send message');
    }
  };

  const handleViewMessage = async (message: Message) => {
    setSelectedMessage(message);
    setViewDialogOpen(true);

    // Mark as read if not already read
    if (!message.isRead) {
      try {
        await markAsReadMutation(message.id).unwrap();
      } catch (error) {
        console.error('Failed to mark message as read:', error);
      }
    }
  };

  const handleStarMessage = (messageId: string) => {
    setMessageUiById((prev) => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        isStarred: !(prev[messageId]?.isStarred ?? false),
      },
    }));
  };

  const handleArchiveMessage = (messageId: string) => {
    setMessageUiById((prev) => ({
      ...prev,
      [messageId]: {
        ...prev[messageId],
        isArchived: !(prev[messageId]?.isArchived ?? false),
      },
    }));
  };

  const handleDeleteMessage = (messageId: string) => {
    setHiddenMessageIds((prev) => new Set(prev).add(messageId));
  };

  // Filter messages
  const filteredMessages = messages.filter((msg) => {
    if (filter === 'unread') return !msg.isRead;
    if (filter === 'starred') return msg.isStarred;
    if (filter === 'archived') return msg.isArchived;
    return !msg.isArchived;
  });

  // Calculate statistics
  const unreadCount = messages.filter((msg) => !msg.isRead).length;
  const starredCount = messages.filter((msg) => msg.isStarred).length;
  const totalMessages = messages.length;

  const getOtherParties = () => {
    // Get other roles based on current user role
    const roles: Array<{ value: 'buyer' | 'seller' | 'agent'; label: string }> =
      [];

    if (userRole !== 'buyer') {
      roles.push({ value: 'buyer', label: 'Buyer' });
    }
    if (userRole !== 'seller') {
      roles.push({ value: 'seller', label: 'Seller' });
    }
    if (userRole !== 'agent' && userRole !== 'seller_agent') {
      roles.push({ value: 'agent', label: 'Agent' });
    }

    return roles;
  };

  return (
    <Box>
      {/* Quick Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<Message />}
                  onClick={() => setComposeDialogOpen(true)}
                >
                  New Message
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Email />}
                  onClick={() =>
                    toast.info(
                      'Email is not wired in-app yet; use your email client.'
                    )
                  }
                >
                  Send Email
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Phone />}
                  onClick={() => toast.info('Calling is not wired in-app yet.')}
                >
                  Call Agent
                </Button>
                {userRole === 'agent' && (
                  <Button
                    variant="outlined"
                    startIcon={<VideoCall />}
                    onClick={() =>
                      toast.info('Video calls are not wired in-app yet.')
                    }
                  >
                    Video Call
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                size="small"
                sx={{ color: 'text.primary' }}
              >
                <InputLabel sx={{ color: 'text.primary' }}>
                  Filter Messages
                </InputLabel>
                <Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  label="Filter Messages"
                >
                  <MenuItem value="all">All Messages</MenuItem>
                  <MenuItem value="unread">Unread Only</MenuItem>
                  <MenuItem value="starred">Starred</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Messages List */}
      <Card>
        <CardHeader
          title="Messages"
          action={
            <Badge badgeContent={unreadCount} color="error">
              <Notifications />
            </Badge>
          }
        />
        <CardContent>
          {filteredMessages.length === 0 ? (
            <Alert severity="info">
              No messages found for the selected filter.
            </Alert>
          ) : (
            <List>
              {filteredMessages.map((message) => (
                <ListItem key={message.id} divider>
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: `${getRoleColor(message.from.role)}.main`,
                      }}
                    >
                      {getRoleIcon(message.from.role)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        flexWrap="wrap"
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: message.isRead ? 'normal' : 'bold',
                            opacity: message.isRead ? 0.7 : 1,
                          }}
                        >
                          {message.subject}
                        </Typography>
                        <Chip
                          label={message.priority}
                          color={getPriorityColor(message.priority) as any}
                          size="small"
                        />
                        {message.attachments &&
                          message.attachments.length > 0 && (
                            <Chip
                              label={`${message.attachments.length} attachment${
                                message.attachments.length > 1 ? 's' : ''
                              }`}
                              color="info"
                              size="small"
                            />
                          )}
                        {!message.isRead && (
                          <Chip label="New" color="error" size="small" />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          From: {message.from.name} ({message.from.role}) •{' '}
                          {new Date(message.timestamp).toLocaleString()}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 1,
                            opacity: message.isRead ? 0.7 : 1,
                          }}
                        >
                          {message.content.length > 100
                            ? `${message.content.substring(0, 100)}...`
                            : message.content}
                        </Typography>
                      </Box>
                    }
                  />
                  <Box>
                    <Tooltip
                      title={
                        message.isStarred
                          ? 'Remove from starred'
                          : 'Add to starred'
                      }
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleStarMessage(message.id)}
                      >
                        {message.isStarred ? (
                          <Star color="warning" />
                        ) : (
                          <StarBorder />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Archive">
                      <IconButton
                        size="small"
                        onClick={() => handleArchiveMessage(message.id)}
                      >
                        <Archive />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMessage(message.id)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => handleViewMessage(message)}
                      >
                        <Message />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Compose Message Dialog */}
      <Dialog
        open={composeDialogOpen}
        onClose={() => setComposeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Compose Message</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>To</InputLabel>
                <Select
                  value={newMessage.toRole}
                  onChange={(e) =>
                    setNewMessage({
                      ...newMessage,
                      toRole: e.target.value as 'buyer' | 'seller' | 'agent',
                    })
                  }
                  label="To"
                >
                  {getOtherParties().map((party) => (
                    <MenuItem key={party.value} value={party.value}>
                      {party.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newMessage.priority}
                  onChange={(e) =>
                    setNewMessage({
                      ...newMessage,
                      priority: e.target.value as 'high' | 'medium' | 'low',
                    })
                  }
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={newMessage.subject}
                onChange={(e) =>
                  setNewMessage({ ...newMessage, subject: e.target.value })
                }
                sx={{ mt: 2 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                multiline
                rows={6}
                value={newMessage.content}
                onChange={(e) =>
                  setNewMessage({ ...newMessage, content: e.target.value })
                }
                sx={{ mt: 2 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSendMessage}
            variant="contained"
            disabled={!newMessage.toRole || !newMessage.content}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Message Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedMessage?.subject}</DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    From: {selectedMessage.from.name} (
                    {selectedMessage.from.role})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    To:{' '}
                    {selectedMessage.to
                      .map((t) => `${t.name} (${t.role})`)
                      .join(', ')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sent: {new Date(selectedMessage.timestamp).toLocaleString()}
                  </Typography>
                  <Box display="flex" gap={1} mt={1}>
                    <Chip
                      label={selectedMessage.priority}
                      color={getPriorityColor(selectedMessage.priority) as any}
                      size="small"
                    />
                    {selectedMessage.attachments &&
                      selectedMessage.attachments.length > 0 && (
                        <Chip
                          label={`${
                            selectedMessage.attachments.length
                          } attachment${
                            selectedMessage.attachments.length > 1 ? 's' : ''
                          }`}
                          color="info"
                          size="small"
                        />
                      )}
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedMessage.content}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button variant="outlined" startIcon={<Reply />}>
            Reply
          </Button>
          <Button variant="outlined" startIcon={<Forward />}>
            Forward
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommunicationHub;
